import cookie from "react-cookies"
import isBrowser from "../utilities/isBrowser"

// Identifiants lus dans l'URL et envoyés tels quels (identifiant nu).
const BARE_URL_PARAMS = ["gclid", "msclkid", "ttclid", "li_fat_id", "rdt_cid"]

// Cookies posés par les tags publicitaires, envoyés avec leur enrobage.
// Ces cookies n'existent qu'une fois le tag correspondant exécuté, donc
// seulement après consentement.
const VENDOR_COOKIES = {
  fbc: "_fbc",
  fbp: "_fbp",
  ttp: "_ttp"
}

// Repli cookie pour gclid. Google enrobe la valeur en "GCL.<timestamp>.<valeur>",
// il faut la déballer car les destinations attendent l'identifiant nu.
const GCLID_COOKIE = "_gcl_aw"

const STORAGE_COOKIE = "agicap_click_ids"
const STORAGE_MAX_AGE = 90 * 24 * 60 * 60

// Valeurs retenues pour la visite. Tant que le consentement n'est pas accordé
// elles ne vivent qu'ici, rien n'est écrit sur le disque du visiteur.
let captured = {}
let landedAt = null
let hasCaptured = false
let hasPersisted = false

/**
 * Retire l'enrobage "GCL.<timestamp>." du cookie _gcl_aw.
 * @param {string} value
 * @returns {string|null}
 */
const unwrapGclAw = value => {
  if (!value) return null
  const match = /^GCL\.\d+\.(.+)$/.exec(value)
  return match ? match[1] : value
}

/**
 * Le consentement Axeptio est accordé lorsque le parcours de la bannière est
 * terminé. Avant ça, aucun tag publicitaire n'a tourné et rien ne doit être
 * persisté.
 * @returns {boolean}
 */
const hasConsent = () => {
  const choices = cookie.load("axeptio_cookies")
  if (!choices) return false
  if (typeof choices === "object") return choices.$$completed === true

  try {
    return JSON.parse(choices).$$completed === true
  } catch (e) {
    return false
  }
}

/**
 * @returns {Object} identifiants présents dans la query string
 */
const readFromUrl = () => {
  const params = new URLSearchParams(window.location.search)
  const found = {}

  BARE_URL_PARAMS.forEach(name => {
    const value = params.get(name)
    if (value) found[name] = value
  })

  // Meta attend la valeur complète du cookie _fbc. Quand le pixel n'a pas
  // encore tourné (avant consentement) ce cookie n'existe pas, on reconstruit
  // alors la chaîne à partir du fbclid de l'URL, au format attendu par CAPI.
  const fbclid = params.get("fbclid")
  if (fbclid) found.fbc = `fb.1.${landedAt}.${fbclid}`

  return found
}

/**
 * @returns {Object} identifiants présents dans les cookies des régies
 */
const readFromVendorCookies = () => {
  const found = {}

  Object.keys(VENDOR_COOKIES).forEach(key => {
    const value = cookie.load(VENDOR_COOKIES[key])
    if (value) found[key] = value
  })

  const gclid = unwrapGclAw(cookie.load(GCLID_COOKIE))
  if (gclid) found.gclid = gclid

  return found
}

/**
 * @returns {Object} identifiants persistés lors d'une visite précédente
 */
const readFromStorage = () => {
  const stored = cookie.load(STORAGE_COOKIE)
  if (!stored) return {}
  if (typeof stored === "object") return stored

  try {
    return JSON.parse(stored)
  } catch (e) {
    return {}
  }
}

/**
 * Écrit les identifiants retenus, uniquement si le consentement est accordé.
 */
const persist = () => {
  if (hasPersisted) return
  if (!hasConsent()) return
  if (Object.keys(captured).length === 0) return

  cookie.save(STORAGE_COOKIE, JSON.stringify(captured), {
    path: "/",
    maxAge: STORAGE_MAX_AGE,
    sameSite: "lax"
  })
  hasPersisted = true
}

/**
 * Identifiants à joindre aux events, recalculés à chaque lecture pour tenir
 * compte des cookies de régie apparus depuis le consentement.
 * @returns {Object}
 */
export const getClickIds = () => {
  if (!isBrowser() || !hasCaptured) return {}

  // Priorité : URL de la visite en cours, puis cookies des régies, puis
  // valeurs d'une visite précédente.
  captured = {
    ...readFromStorage(),
    ...readFromVendorCookies(),
    ...captured
  }

  persist()

  return { ...captured }
}

/**
 * Capture au premier chargement, avant que Segment ne soit chargé.
 * L'URL est la source primaire, elle porte la valeur la plus récente.
 */
export const captureClickIds = () => {
  if (!isBrowser() || hasCaptured) return

  landedAt = Date.now()
  hasCaptured = true

  captured = {
    ...readFromStorage(),
    ...readFromVendorCookies(),
    ...readFromUrl()
  }

  persist()

  // Lu par le middleware Segment, qui n'est pas dans le bundle et ne peut pas
  // importer ce module. Fonction et non objet, pour que chaque event reparte
  // d'une lecture fraîche.
  window.__agicap_get_click_ids = getClickIds

  // Le consentement arrive après la capture : on persiste dès qu'il est donné.
  window._axcb = window._axcb || []
  window._axcb.push(axeptio => {
    axeptio.on("cookies:complete", () => {
      getClickIds()
    })
  })
}
